import { debounce } from 'lodash';
import { forwardRef, PropsWithChildren, Ref, useCallback, useEffect, useState } from 'react';
import { OrNull } from '../TextEditor/Slate/components';

export interface InputFieldProps {
  value: string | number;
  label?: string;
  name?: string;
  placeholder?: string;
  autoFocus?: boolean;
  autoComplete?:string;
  type?: string;
  valid?: boolean;
  onChange?: (e) => void;
  onKeyDown?: (e) => void;
  onBlur?: (e) => void;
  onFocus?: (e) => void;
  readOnly?: boolean;
  className?: string;
  inputClassName?: string;
  min?: number;
  max?: number;
}

const InputField = forwardRef(
  (
    {
      value,
      label,
      valid = true,
      name,
      placeholder,
      readOnly = false,
      type = 'text',
      onChange,
      onKeyDown,
      onBlur,
      onFocus,
      className,
      autoFocus,
      autoComplete,
      inputClassName,
      min,
      max,
      children,
    }: PropsWithChildren<InputFieldProps>,
    ref: Ref<OrNull<HTMLInputElement>>,
  ) => {
    const [text, setText] = useState(value);

    const onChangeText = e => {
      setText(e.target.value);
      debouncedUpdate(e.target.value);
    };

    const debouncedUpdate = useCallback(
      debounce(val => onChange(val), 1000),
      [],
    );

    useEffect(() => setText(value), [value]);

    return (
      <div className={className}>
        {label && (
          <label className="block w-full" htmlFor="input-field">
            {label}
          </label>
        )}
        <input
          min={min}
          max={max}
          ref={ref}
          readOnly={readOnly}
          type={type}
          value={text}
          name={name}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          placeholder={placeholder}
          onFocus={e => (onFocus ? onFocus(e.target.value) : null)}
          onBlur={e => (onBlur ? onBlur(e.target.value) : null)}
          onKeyDown={e => (onKeyDown ? onKeyDown(e) : null)}
          onChange={onChangeText}
          className={` text-inherit border-none bg-transparent focus:border-none focus:ring-none focus:outline-none outline-none ring-none ${
            valid ? '' : 'border-red-600'
          } rounded-md ${inputClassName}`}
        />
        {children}
      </div>
    );
  },
);

export default InputField;
